import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { ArrowLeft, Bot, Bell, Zap, BarChart3, Clock, Loader2 } from "lucide-react";

function SetupTab({ settings, onSave }) {
  const [aiName, setAiName] = useState(settings?.ai_name || "Asistente");
  const [welcome, setWelcome] = useState(settings?.welcome_message || "");
  const [humanMsg, setHumanMsg] = useState(settings?.human_transfer_message || "");
  const [activateKw, setActivateKw] = useState((settings?.activate_keywords || []).join(", "));
  const [deactivateKw, setDeactivateKw] = useState((settings?.deactivate_keywords || []).join(", "));
  const [persistMessages, setPersistMessages] = useState(settings?.persist_messages ?? true);
  const [managerPhone, setManagerPhone] = useState(settings?.manager_phone || "");
  const [handoffEnabled, setHandoffEnabled] = useState(settings?.human_handoff_enabled ?? true);
  const [autoResumeMin, setAutoResumeMin] = useState(settings?.human_handoff_auto_resume_minutes ?? 30);

  return (
    <div className="space-y-5 p-4">
      <div className="flex items-center justify-between border rounded-lg p-3">
        <div>
          <p className="text-sm font-medium">Persistir mensajes recibidas y enviadas</p>
          <p className="text-xs text-gray-400">Almacenar historial de conversaciones.</p>
        </div>
        <Switch checked={persistMessages} onCheckedChange={setPersistMessages} />
      </div>
      <div>
        <label className="text-sm font-bold text-gray-700 block mb-1">Nombre de la IA</label>
        <Input value={aiName} onChange={e => setAiName(e.target.value)} placeholder="Carol, Ana..." data-testid="ai-name-input" />
        <p className="text-xs text-gray-400 mt-1">El nombre que la IA usa para presentarse.</p>
      </div>
      <div>
        <label className="text-sm font-bold text-gray-700 block mb-1">WhatsApp del Gerente / Dueno</label>
        <Input value={managerPhone} onChange={e => setManagerPhone(e.target.value)} placeholder="+34673036835" data-testid="manager-phone-input" />
        <p className="text-xs text-gray-400 mt-1">Recibe el cierre de caja y notificaciones importantes en tiempo real.</p>
      </div>
      <div>
        <label className="text-sm font-bold text-gray-700 block mb-1">Mensaje de bienvenida</label>
        <textarea className="w-full border rounded-lg p-3 text-sm min-h-[80px] resize-none" value={welcome} onChange={e => setWelcome(e.target.value)} data-testid="welcome-msg-input" />
        <p className="text-xs text-gray-400 mt-1">Variables: {"{{first_name}}"}, {"{{ai_name}}"}</p>
      </div>
      <div>
        <label className="text-sm font-bold text-gray-700 block mb-1">Palabras de ACTIVACION</label>
        <Input value={activateKw} onChange={e => setActivateKw(e.target.value)} placeholder="entrar, menu, pedido" data-testid="activate-keywords" />
      </div>
      <div className="border rounded-lg p-3 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-bold text-gray-700">Atención humana (Human Handoff)</p>
            <p className="text-xs text-gray-400">La IA deja de responder y un humano puede asumir.</p>
          </div>
          <Switch checked={handoffEnabled} onCheckedChange={setHandoffEnabled} data-testid="handoff-enabled" />
        </div>
        {handoffEnabled && (
          <>
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Palabras para pausar IA</label>
              <Input value={deactivateKw} onChange={e => setDeactivateKw(e.target.value)} placeholder="sair, humano, atendente" data-testid="deactivate-keywords" />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs font-medium text-gray-600 whitespace-nowrap">Volver automático en</label>
              <Input type="number" min={1} max={1440} className="w-20 h-8 text-sm" value={autoResumeMin}
                onChange={e => setAutoResumeMin(parseInt(e.target.value) || 30)} data-testid="auto-resume-minutes" />
              <span className="text-xs text-gray-500">minutos</span>
            </div>
            <p className="text-xs text-gray-400">Pasado este tiempo la IA vuelve automáticamente. Para reactivar antes: escribe <strong>ENTRAR</strong>.</p>
          </>
        )}
        {!handoffEnabled && (
          <div>
            <label className="text-xs font-medium text-gray-600 block mb-1">Palabras de DESACTIVACION</label>
            <Input value={deactivateKw} onChange={e => setDeactivateKw(e.target.value)} placeholder="sair, humano, atendente" data-testid="deactivate-keywords" />
          </div>
        )}
      </div>
      <div>
        <label className="text-sm font-bold text-gray-700 block mb-1">Mensaje de transferencia a humano</label>
        <textarea className="w-full border rounded-lg p-3 text-sm min-h-[60px] resize-none" value={humanMsg} onChange={e => setHumanMsg(e.target.value)} />
      </div>
      <Button className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="save-setup"
        onClick={() => onSave({
          ai_name: aiName, welcome_message: welcome, human_transfer_message: humanMsg,
          persist_messages: persistMessages, manager_phone: managerPhone,
          activate_keywords: activateKw.split(",").map(k => k.trim()).filter(Boolean),
          deactivate_keywords: deactivateKw.split(",").map(k => k.trim()).filter(Boolean),
          human_handoff_enabled: handoffEnabled,
          human_handoff_auto_resume_minutes: autoResumeMin,
        })}>Guardar Setup</Button>
    </div>
  );
}

function NotificationsTab({ settings, onSave }) {
  const notifs = settings?.notifications || {};
  const [state, setState] = useState({
    order_received: { enabled: notifs.order_received?.enabled ?? true, message: notifs.order_received?.message || "" },
    order_preparing: { enabled: notifs.order_preparing?.enabled ?? true, message: notifs.order_preparing?.message || "" },
    order_out_delivery: { enabled: notifs.order_out_delivery?.enabled ?? true, message: notifs.order_out_delivery?.message || "" },
    order_delivered: { enabled: notifs.order_delivered?.enabled ?? true, message: notifs.order_delivered?.message || "" },
    payment_reminder: { enabled: notifs.payment_reminder?.enabled ?? true, delay_minutes: notifs.payment_reminder?.delay_minutes || 10, message: notifs.payment_reminder?.message || "" },
  });
  const labels = {
    order_received: "Pedido recibido",
    order_preparing: "Pedido en preparación",
    order_out_delivery: "Salió para entrega",
    order_delivered: "Pedido entregado",
    payment_reminder: "Recordatorio de pago",
  };
  const update = (key, field, val) => setState(p => ({ ...p, [key]: { ...p[key], [field]: val } }));

  return (
    <div className="space-y-4 p-4">
      <p className="text-xs text-gray-400">Variables: {"{{first_name}}"}, {"{{order_id}}"}, {"{{points}}"}, {"{{payment_link}}"}</p>
      {Object.entries(state).map(([key, val]) => (
        <div key={key} className="border rounded-lg p-3 space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bell className="w-4 h-4 text-emerald-600" />
              <span className="font-bold text-sm">{labels[key]}</span>
            </div>
            <Switch checked={val.enabled} onCheckedChange={v => update(key, "enabled", v)} />
          </div>
          {key === "payment_reminder" && (
            <div className="flex items-center gap-2">
              <Clock className="w-3 h-3 text-gray-400" />
              <span className="text-xs text-gray-500">Enviar después de</span>
              <Input type="number" className="w-16 h-7 text-xs" value={val.delay_minutes} onChange={e => update(key, "delay_minutes", parseInt(e.target.value) || 10)} />
              <span className="text-xs text-gray-500">minutos</span>
            </div>
          )}
          <textarea className="w-full border rounded-lg p-2 text-sm min-h-[50px] resize-none" value={val.message} onChange={e => update(key, "message", e.target.value)} />
        </div>
      ))}
      <Button className="w-full bg-emerald-600 hover:bg-emerald-700" onClick={() => onSave({ notifications: state })} data-testid="save-notifications">Guardar Notificaciones</Button>
    </div>
  );
}

function ActivationsTab({ settings, onSave }) {
  const acts = settings?.activations || {};
  const periods = [
    { key: "day_1", label: "1 día sin comprar" },
    { key: "day_15", label: "15 días sin comprar" },
    { key: "day_30", label: "30 días sin comprar" },
    { key: "day_60", label: "60 días sin comprar" },
    { key: "day_180", label: "180 días sin comprar" },
  ];
  const [state, setState] = useState(
    periods.reduce((acc, p) => ({ ...acc, [p.key]: { enabled: acts[p.key]?.enabled ?? false, message: acts[p.key]?.message || "" } }), {})
  );
  const update = (key, field, val) => setState(p => ({ ...p, [key]: { ...p[key], [field]: val } }));

  return (
    <div className="space-y-4 p-4">
      <p className="text-xs text-gray-400">Mensajes automáticos para re-activar clientes inactivos. Variables: {"{{first_name}}"}</p>
      {periods.map(({ key, label }) => (
        <div key={key} className="border rounded-lg p-3 space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-amber-500" />
              <span className="font-bold text-sm">{label}</span>
            </div>
            <Switch checked={state[key]?.enabled} onCheckedChange={v => update(key, "enabled", v)} />
          </div>
          <textarea className="w-full border rounded-lg p-2 text-sm min-h-[50px] resize-none" value={state[key]?.message || ""} onChange={e => update(key, "message", e.target.value)} />
        </div>
      ))}
      <Button className="w-full bg-emerald-600 hover:bg-emerald-700" onClick={() => onSave({ activations: state })} data-testid="save-activations">Guardar Activaciones</Button>
    </div>
  );
}

function CashClosingTab({ settings, onSave, authAxios }) {
  const cc = settings?.cash_closing || {};
  const [enabled, setEnabled] = useState(cc.enabled ?? false);
  const [phones, setPhones] = useState((cc.send_to_phones || []).join(", "));
  const [emails, setEmails] = useState((cc.send_to_emails || []).join(", "));
  const [time, setTime] = useState(cc.send_time || "23:00");
  const [sending, setSending] = useState(false);
  const [lastReport, setLastReport] = useState(null);

  const handleSendNow = async () => {
    setSending(true);
    try {
      const res = await authAxios.post("/chatbot/cash-closing/send");
      if (res.data.success) {
        const parts = [];
        if (res.data.whatsapp_sent > 0) parts.push(`${res.data.whatsapp_sent} WhatsApp`);
        if (res.data.email_sent > 0) parts.push(`${res.data.email_sent} email(s)`);
        toast.success(parts.length > 0 ? `Cierre enviado: ${parts.join(", ")}` : "Cierre generado (sin destinatarios conectados)");
        setLastReport(res.data.report_preview);
      } else { toast.error(res.data.error || "Error al enviar"); }
    } catch { toast.error("Error al enviar cierre de caja"); }
    setSending(false);
  };

  return (
    <div className="space-y-5 p-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-bold text-sm">Fechamento de Caixa</h3>
          <p className="text-xs text-gray-400">Envia un resumen diario por WhatsApp y/o email automaticamente.</p>
        </div>
        <Switch checked={enabled} onCheckedChange={setEnabled} />
      </div>
      <div className="bg-gray-50 rounded-lg p-4 text-sm space-y-2">
        <p className="font-medium">El resumen incluye:</p>
        <ul className="text-xs text-gray-500 space-y-1 list-disc ml-4">
          <li>Total de pedidos del dia</li>
          <li>Facturacion total</li>
          <li>Ticket medio</li>
          <li>Pedidos por canal (mesa vs delivery)</li>
          <li>Top productos vendidos</li>
          <li>Metodos de pago</li>
          <li>Evaluaciones recibidas</li>
        </ul>
      </div>
      <div>
        <label className="text-sm font-bold text-gray-700 block mb-1">Telefonos WhatsApp</label>
        <Input value={phones} onChange={e => setPhones(e.target.value)} placeholder="+34612345678, +34698765432" data-testid="cash-closing-phones" />
        <p className="text-xs text-gray-400 mt-1">Separados por coma. Gerentes y duenos.</p>
      </div>
      <div>
        <label className="text-sm font-bold text-gray-700 block mb-1">Emails de destino</label>
        <Input value={emails} onChange={e => setEmails(e.target.value)} placeholder="gerente@restaurante.com" data-testid="cash-closing-emails" />
        <p className="text-xs text-gray-400 mt-1">Separados por coma. Reciben el cierre en formato HTML.</p>
      </div>
      <div>
        <label className="text-sm font-bold text-gray-700 block mb-1">Hora de envio automatico</label>
        <Input type="time" value={time} onChange={e => setTime(e.target.value)} className="w-32" data-testid="cash-closing-time" />
        <p className="text-xs text-gray-400 mt-1">El cierre se envia automaticamente a esta hora todos los dias.</p>
      </div>
      <Button className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="save-cash-closing"
        onClick={() => onSave({ cash_closing: { enabled, send_to_phones: phones.split(",").map(p => p.trim()).filter(Boolean), send_to_emails: emails.split(",").map(e => e.trim()).filter(Boolean), send_time: time } })}>
        Guardar Fechamento
      </Button>
      <Button variant="outline" className="w-full gap-2" onClick={handleSendNow} disabled={sending} data-testid="send-cash-closing-now">
        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <BarChart3 className="w-4 h-4" />}
        Enviar cierre de caja ahora
      </Button>
      {lastReport && (
        <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-600 whitespace-pre-wrap border" data-testid="cash-closing-preview">
          {lastReport}
        </div>
      )}
    </div>
  );
}

export function ChatBotSettingsPanel({ botSettings, saveSettings, authAxios, onClose }) {
  const [settingsTab, setSettingsTab] = useState("setup");

  return (
    <div className="absolute inset-0 bg-white z-50 flex flex-col" data-testid="chatbot-settings">
      <div className="bg-emerald-600 text-white px-4 py-3 flex items-center gap-2">
        <button onClick={onClose}><ArrowLeft className="w-5 h-5" /></button>
        <h2 className="font-bold text-sm">Configuración del ChatBot</h2>
      </div>
      <Tabs value={settingsTab} onValueChange={setSettingsTab} className="flex-1 flex flex-col">
        <TabsList className="flex justify-start border-b bg-white px-2 rounded-none flex-shrink-0 overflow-x-auto">
          <TabsTrigger value="setup" className="text-xs gap-1"><Bot className="w-3 h-3" /> Setup</TabsTrigger>
          <TabsTrigger value="notifications" className="text-xs gap-1"><Bell className="w-3 h-3" /> Notificaciones</TabsTrigger>
          <TabsTrigger value="activations" className="text-xs gap-1"><Zap className="w-3 h-3" /> Activaciones</TabsTrigger>
          <TabsTrigger value="cash_closing" className="text-xs gap-1"><BarChart3 className="w-3 h-3" /> Cierre Caja</TabsTrigger>
        </TabsList>
        <div className="flex-1 overflow-y-auto">
          <TabsContent value="setup" className="mt-0"><SetupTab settings={botSettings} onSave={saveSettings} /></TabsContent>
          <TabsContent value="notifications" className="mt-0"><NotificationsTab settings={botSettings} onSave={saveSettings} /></TabsContent>
          <TabsContent value="activations" className="mt-0"><ActivationsTab settings={botSettings} onSave={saveSettings} /></TabsContent>
          <TabsContent value="cash_closing" className="mt-0"><CashClosingTab settings={botSettings} onSave={saveSettings} authAxios={authAxios} /></TabsContent>
        </div>
      </Tabs>
    </div>
  );
}
